import { Preferences } from '@capacitor/preferences'
import { useEffect, useState } from 'react'
import savePreference from './savePreference'
import { Preferences as PreferencesType, SavePreferences } from './types/preference.interface'

/**
 * Custom hook to manage user preferences.
 * @param key - The key of the preference to manage.
 * @returns A tuple containing the current value and a setter function, similar to useState.
 */
const usePreference = <K extends keyof PreferencesType>(
	key: K,
	defaultValue: PreferencesType[K]
): [PreferencesType[K], (newValue: PreferencesType[K]) => void, boolean] => {
	const [value, setValue] = useState<PreferencesType[K]>(defaultValue)
	const [isLoading, setIsLoading] = useState(true)
	const [isInitialized, setIsInitialized] = useState(false)

	// Load preference from storage on mount
	useEffect(() => {
		const fetchPreference = async () => {
			try {
				const { value: storedValue } = await Preferences.get({ key })

				if (storedValue !== null) {
					try {
						// Use JSON.parse for reliable deserialization
						const parsedValue = JSON.parse(storedValue)
						setValue(parsedValue as PreferencesType[K])
					} catch {
						// Fallback for non-JSON values (legacy support)
						setValue(storedValue as PreferencesType[K])
					}
				}
				// If no stored value, keep the defaultValue that was set in useState
			} catch (error) {
				console.error(`Failed to fetch preference for key "${String(key)}":`, error)
			} finally {
				setIsLoading(false)
				setIsInitialized(true)
			}
		}

		fetchPreference()
	}, [key])

	// Save preference to storage when value changes (only after initialization)
	useEffect(() => {
		if (!isInitialized) {
			return // Skip saving until we've loaded the initial value
		}

		const saveValue = async () => {
			try {
				// Use JSON.stringify for reliable serialization
				const stringifiedValue = value === null ? null : JSON.stringify(value)
				await savePreference({ key, value: stringifiedValue } as SavePreferences)
			} catch (error) {
				console.error(`Failed to save preference for key "${String(key)}":`, error)
			}
		}

		saveValue()
	}, [value, isInitialized, key])

	return [value, setValue, isLoading]
}

export default usePreference
